import { AfterViewInit, Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { AddressesProvider } from '../../../addresses/providers/addresses-provider';
import { FilesProvider } from '../../../files/providers/files-provider';
import { CoachesProvider, CoachRow } from '../../providers/coaches-provider';
import { CoachRights } from '../../providers/coaches-rights';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef, MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AthletesProvider } from "../../../athletes/providers/athletes-provider";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { Team } from "../../../common/models/team";
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { Lang } from "../../../common/models/lang";
import { Coach } from "../../../common/models/coach";
import { map, Observable, switchMap } from "rxjs";
import { InputImageComponent } from "../../../common/components/input-image/input-image";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatError, MatFormField } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatProgressBar } from "@angular/material/progress-bar";
import { NgIf } from "@angular/common";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatInput } from "@angular/material/input";
import { MatTooltip } from "@angular/material/tooltip";

@Component({
  selector: 'coaches-form',
  providers: [],
  styleUrls: ['./form.scss'],
  standalone: true,
  imports: [
    MatDialogContent,
    MatDialogActions,
    InputImageComponent,
    MatCheckbox,
    MatError,
    MatFormField,
    MatIcon,
    MatProgressBar,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogClose,
    MatDialogTitle,
    NgIf,
    MatButton,
    MatIconButton,
    MatInput,
    MatTooltip,
  ],
  templateUrl: './form.html'
})
export class CoachesFormComponent implements AfterViewInit {
    public readonly NAME_MAX_LENGTH: number = 200;
    public readonly CHECK_EMAIL_THRESHOLD: number = 750;
    public isLoading = false;
    public formTitle = '';

    public availableTeams: Team[] = [];
    public availableAthletes: User[] = [];

    public organization: Organization | null = null;
    public coachId = 0;
    public user: User = new User({});

    public langId: number = Lang.ID_DEFAULT;

    public avatarImage: any = { current: null, new: null };

    public firstnameFormControl: FormControl = new FormControl('', [
        Validators.required
    ]);
    public lastnameFormControl: FormControl = new FormControl('', [
        Validators.required
    ]);
    public emailUnavailableError = false;
    public emailFormControl: FormControl = new FormControl('', [
        Validators.required,
        Validators.email,
        (control: any) => this.emailUnavailableError ? { unavailable: { valid: false } } : null
    ]);
    public phoneNumberFormControl: FormControl = new FormControl('');
    public isSuperCoach = false;
    public isLimited = false;
    public rights: CoachRights = new CoachRights(this.utils);

    private _checkEmailAvailabilityRequestTimestamp = 0;
    private _originalEmail: string | null = null;
    constructor(
        public conf: ConfigProvider,
        public currentUser: CurrentUser,
        public utils: UtilsProvider,
        public dialog: MatDialogRef<CoachesFormComponent>,
        public usersProvider: UsersProvider,
        private _toaster: MatSnackBar,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _coachesProvider: CoachesProvider,
        private _translater: TranslateService,
        @Inject(MAT_DIALOG_DATA) private _data: any
    ) {
        this.availableAthletes = _data.availableAthletes;
        this.availableTeams = _data.availableTeams;
        this.organization = _data.organization;
        this.formTitle = this._data.formTitle;
        this.isSuperCoach = this._data.itsASuperCoach;

        this.rights.evaluateScreenRights({
            rolesIds: this._currentUserRoles.getRolesIds(this.organization?.id!)
        });

        if (_data.coachRow) {

            this.coachId = this._data.coachRow.coach.id;
            this.user = this._data.coachRow.user;
            this.langId = this._data.coachRow.user.langId;

            this.firstnameFormControl.setValue(this._data.coachRow.user.firstName);
            this.lastnameFormControl.setValue(this._data.coachRow.user.lastName);
            this.emailFormControl.setValue(this._data.coachRow.user.email);
            this._originalEmail = this._data.coachRow.user.email;
            this.phoneNumberFormControl.setValue(this._data.coachRow.user.phoneNumber);
            this.isSuperCoach = this._data.coachRow.isSuperCoach;
            this.isLimited = this._data.coachRow.isLimitedCoach && !this.isSuperCoach;

            this.avatarImage.current = _data.coachRow.user.avatar;
        }
    }

    public ngAfterViewInit() {
    }

    // A super coach can always create athletes, so the limit cannot apply to them.
    public onSuperCoachChange(isSuperCoach: boolean): void {
        this.isSuperCoach = isSuperCoach;
        if (isSuperCoach) {
            this.isLimited = false;
        }
    }

    public onSaveClicked(): void {
        if (this.canSave()) {
            this.isLoading = true;
            this.save().subscribe({
                next: (coachRow: CoachRow) => {
                    this._toaster.open(this._translater.instant('Coach_saved'), '', { duration: 5000 });
                    this.isLoading = false;
                    this.dialog.close(coachRow);
                },
                error: (error: any) => {
                  this.onError(error);
                },
            });
        } else {
            this.touchFields();
            this._toaster.open(this._translater.instant('Correct_form_errors'), '', { duration: 5000 });
        }
    }

    public canSave(): boolean {
        return !this.isLoading
            && !this.firstnameFormControl.invalid
            && !this.lastnameFormControl.invalid
            && !this.emailFormControl.invalid
            && !this.phoneNumberFormControl.invalid;
    }

    public onEmailChanged(): void {
        this.emailUnavailableError = false;
        this.emailFormControl.markAsTouched();
        this.emailFormControl.updateValueAndValidity();
        if (!this.emailFormControl.hasError('email') && this.emailFormControl.value != this._originalEmail) {
            // on check la dispo que si l'email est valide et différente de l'originale ...
            this.checkEmailAvailability();
        }
    }

    private touchFields(): void {
        this.firstnameFormControl.markAsTouched();
        this.lastnameFormControl.markAsTouched();
        this.phoneNumberFormControl.markAsTouched();
        this.emailFormControl.markAsTouched();
    }

    private checkEmailAvailability(): void {
        this._checkEmailAvailabilityRequestTimestamp = (new Date()).getTime();
        setTimeout(() => {
            const now: number = (new Date()).getTime();
            if (now - this._checkEmailAvailabilityRequestTimestamp >= this.CHECK_EMAIL_THRESHOLD) {
                this.usersProvider.checkEmailAvailability({email: this.emailFormControl.value, deep: false}).subscribe({
                    next: (response: any) => {
                        this.emailUnavailableError = !response;
                        this.emailFormControl.updateValueAndValidity();
                    },
                    error: (error: any) => {
                        this.emailUnavailableError = true;
                        this.emailFormControl.updateValueAndValidity();
                        this.onError(error);
                    }
                })
            }
        }, this.CHECK_EMAIL_THRESHOLD);
    }

    private save(): Observable<CoachRow> {
      // Update user properties

      const coach = {} as Coach;
      coach.id = this.coachId;
      coach.userId = this.user.id;
      this.user.firstName = this.firstnameFormControl.value;
      this.user.lastName = this.lastnameFormControl.value;
      this.user.email = this.emailFormControl.value;
      this.user.phoneNumber = this.phoneNumberFormControl.value;
      this.user.isAdmin = 0;
      this.user.langId = this.currentUser.user!.langId;
      this.user.avatar = this.avatarImage.new || this.avatarImage.current || undefined;

      const coachRow = {
        coach,
        user: this.user,
        isSuperCoach: this.isSuperCoach,
        isLimitedCoach: this.isLimited,
      } as CoachRow;
      return this._coachesProvider.saveCoach(this.organization!.id!, coachRow).pipe(
        map((coachRow: CoachRow) => {
          this.user = coachRow.user;
          return coachRow;
        })
      );
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 422) {
            if (error.error.errors && error.error.errors.email) {
                // c'est qu'on a essayé de faire valider un email test@test par exemple (c'est validé par le validator angular mais pas laravel...)
                return;
            }
        }

        this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
    }

}
